import React, { useState } from "react";
import moment from "moment";
import { Stack, Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";

import { IconButton, Textarea, Tooltip } from "../../../../components";
import { BORDER_COLORS, DATETIME_FORMAT } from "../../../../constants";
import { useAppDispatch } from "../../../../store/hooks";
import {
  createNoteThunk,
  deleteNoteThunk,
  updateNoteThunk,
} from "../../../../features/productAnalyzer/productAnalyzer.thunk";
import { INote } from "../../../../features/productAnalyzer/productAnalyzer.types";
import { colors } from "../../AmazonEmbededView/config";

interface INoteItemProps {
  asin: string;
  data?: INote;
  isEmbed?: boolean;
  onClose?: () => void;
}

const embedExtensionIconStyle = {
  backgroundColor: colors.primary.main,
  border: `1px solid ${colors.primary.main}`,
  color: colors.text.white,

  "&:hover": {
    backgroundColor: colors.primary.hover,
    borderColor: colors.primary.hover,
  },
};

export const NoteItem: React.FC<INoteItemProps> = ({
  asin,
  data,
  onClose,
  isEmbed,
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState<boolean>(!data);
  const [editContent, setEditContent] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleEditClick = () => {
    setEditContent(data?.note || "");
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (editContent.trim()) {
      setIsSaving(true);

      if (data) {
        dispatch(
          updateNoteThunk({
            asin,
            id: data.id,
            note: editContent,
          }),
        )
          .unwrap()
          .then(() => {
            setIsSaving(false);
            setIsEditing(false);
          });
      } else {
        dispatch(createNoteThunk({ asin, note: editContent }))
          .unwrap()
          .then((_) => {
            setIsSaving(false);
            onClose?.();
          });
      }
    }
  };

  const handleEditContentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setEditContent(event.target.value);
  };

  const handleDeleteClick = () => {
    if (data) {
      setIsDeleting(true);
      dispatch(deleteNoteThunk({ id: data.id, asin }))
        .unwrap()
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const handleCancelClick = () => {
    if (data) {
      setIsEditing(false);
    } else {
      onClose?.();
    }
  };

  return (
    <Stack
      direction={"row"}
      sx={{
        gap: "6px",
        width: "100%",
        padding: "4px",
        borderRadius: "4px",
        border: `1px solid ${BORDER_COLORS.default}`,
      }}
    >
      <Box flexGrow={"1"}>
        {data && !isEditing ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              width: "100%",
              height: "100%",
            }}
          >
            <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
              {data.note}
            </Typography>
            <Box textAlign={"right"}>
              <Typography variant="caption">
                {moment(data.createdAt).local().format(DATETIME_FORMAT)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Textarea
            minRows={3}
            value={editContent}
            onChange={handleEditContentChange}
            disabled={isSaving}
            autoFocus
            sx={
              isEmbed
                ? {
                    backgroundColor: colors.background.white,
                    borderColor: colors.border.light,
                    color: colors.text.primary,
                    "&:focus, &:hover, &:active, &:focus-visible": {
                      borderColor: colors.primary.main,
                      outline: "none",
                      boxShadow: `0 0 0 2px ${colors.primary.light}`,
                    },
                  }
                : undefined
            }
          />
        )}
      </Box>
      <Stack direction={"column"} gap={"4px"}>
        {isEditing ? (
          <>
            <Tooltip title="Save">
              <IconButton
                size="small"
                onClick={handleSaveClick}
                loading={isSaving}
                disabled={!editContent.trim()}
                sx={isEmbed ? embedExtensionIconStyle : undefined}
              >
                <SaveOutlinedIcon sx={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                onClick={handleCancelClick}
                disabled={isSaving}
                sx={isEmbed ? embedExtensionIconStyle : undefined}
              >
                <CloseIcon sx={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Edit">
              <IconButton
                color="default"
                size="small"
                onClick={handleEditClick}
                disabled={isDeleting}
              >
                <EditIcon sx={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="default"
                size="small"
                onClick={handleDeleteClick}
                loading={isDeleting}
              >
                <DeleteOutlinedIcon sx={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    </Stack>
  );
};
